import React, { BaseSyntheticEvent, useState } from 'react'
import {
  IonButton,
  IonButtons,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPopover,
  IonRippleEffect,
  IonToolbar,
} from '@ionic/react'
import { IOption, IOptionObj } from '../../common/common.types'
import { caretDownOutline, checkmarkOutline } from 'ionicons/icons'
import { isTablet } from '../../utilities'
import './MultisectionPicklist.component.css'
import { useTranslation } from 'react-i18next'
import { getStyleForPHI, IPHIComponent } from '../phi/phi.component'
import { useSelector } from 'react-redux'
import { getCustomSignaturesSelector } from '../../features/customSignature/selectors/getCustomSignature.selectors'
import { getSignaturesSelector } from '../../features/signatures/selectors/getSignatures.selector'
export interface IDropdownProps<T> extends IPHIComponent {
  dataTestID: string
  listHeader?: string
  options: T[]
  selectedOptions?: any
  currentSelectedOption?: any
  disabled?: boolean
  label?: string
  setSeletedSection?: (selectedSection: IOption) => void
  setSelectedOption: (selectedOption: any) => void
}

interface TypeOfT extends IOption {
  values?: IOptionObj[]
}

const MultisectionPicklist: React.FC<IDropdownProps<TypeOfT>> = (props: IDropdownProps<TypeOfT>) => {
  const { t } = useTranslation()
  const {
    dataTestID,
    listHeader,
    label,
    options = [],
    selectedOptions = [],
    currentSelectedOption,
    isPHI,
    disabled,
    setSeletedSection,
    setSelectedOption,
  } = props
  const [showModal, setShowModal] = useState(false)
  const [popoverState, setShowPopover] = useState({ showPopover: false, event: undefined })
  const isTabletDevice = isTablet()
  const customSignaturesState = useSelector(getCustomSignaturesSelector)
  const { customSignatureClientData } = customSignaturesState
  const { signatureType } = customSignatureClientData
  const signaturesState = useSelector(getSignaturesSelector)
  const { isMark, chips } = signaturesState

  const showDropdown = (event: any) => {
    setShowModal(true)
  }

  const dismiss = () => {
    resetModalState()
    if (isTabletDevice) {
      resetPopoverState()
    } else {
      resetModalState()
    }
  }

  const selectOption = (e: BaseSyntheticEvent, selectedOption: IOptionObj, selectedSection: IOption) => {
    e.preventDefault()
    if (selectedOption.name) {
      setSelectedOption(selectedOption)
    }
    if (setSeletedSection) {
      setSeletedSection({ id: selectedSection.id, name: selectedSection.name })
    }
    setTimeout(() => {
      dismiss()
    }, 250)
  }

  const resetModalState = () => {
    setShowModal(false)
  }

  const resetPopoverState = () => {
    setShowPopover({ showPopover: false, event: undefined })
  }

  const selectedLabelCheck = (optionObj: IOptionObj) => {
    if (signatureType === 'customSignature') {
      return (
        currentSelectedOption &&
        optionObj.name === currentSelectedOption.name &&
        currentSelectedOption.parentID === optionObj.parentID &&
        currentSelectedOption.id === optionObj.id
      )
    }
    return currentSelectedOption && optionObj.name === currentSelectedOption.name
  }

  const isCheckMarkCheck = (optionObj: IOptionObj) => {
    if (signatureType === 'customSignature') {
      return selectedOptions.some(
        (chipObject: any) => optionObj?.id === chipObject?.signatureId && chipObject?.formId === optionObj?.parentID
      )
    }

    return selectedOptions.some((chipObject: any) => optionObj.id === chipObject.option.id)
  }

  const content = (
    <>
      {listHeader && (
        <IonHeader className='ion-no-border'>
          <IonToolbar color='light' className='p-4'>
            <IonLabel>{listHeader}</IonLabel>
          </IonToolbar>
        </IonHeader>
      )}

      <IonList>
        {options.length &&
          options.map((option) => {
            const sectionId = selectedOptions.length && selectedOptions[0].id
            const disabled = Boolean(selectedOptions.length && option.id !== sectionId)

            return (
              <React.Fragment key={option.name}>
                <IonHeader className=''>
                  <IonToolbar
                    className={`${disabled && signatureType === 'standardSignature' ? 'disabled-toolbar' : ''}`}>
                    <IonLabel className='header-text'>{option.name}</IonLabel>
                  </IonToolbar>
                </IonHeader>

                {option.values
                  ?.filter((optionObj) => {
                    const isWitness = optionObj.signatureType === 'Witness'

                    const hasPatientSignature = chips?.some((chip) => chip?.option?.signatureType === 'Patient')

                    if (isWitness && !hasPatientSignature) {
                      return false
                    }

                    if (isWitness && isMark !== 1 && signatureType === 'standardSignature') {
                      return false
                    }

                    return true
                  })
                  .map((optionObj) => {
                    const isCheckMark = isCheckMarkCheck(optionObj)
                    return (
                      <IonItem
                        key={`option_${optionObj.id}`}
                        disabled={disabled && signatureType === 'standardSignature'}
                        data-private={isPHI}
                        style={getStyleForPHI(isPHI)}
                        button
                        detail={false}
                        data-testid={`Option_${optionObj.id}`}
                        onClick={(e) => selectOption(e, optionObj, option)}
                        className='ion-no-padding ion-activatable'>
                        {isCheckMark && <IonIcon className='padding-left' size='small' icon={checkmarkOutline} />}
                        <IonLabel
                          className={`optionLabel ${selectedLabelCheck(optionObj) ? 'selectedLabel' : ''} ${
                            !isCheckMark ? 'marginLeft' : ''
                          }`}>
                          {optionObj.name}
                        </IonLabel>
                        <IonRippleEffect />
                      </IonItem>
                    )
                  })}
              </React.Fragment>
            )
          })}
      </IonList>
      <IonFooter className='ion-no-border'>
        <IonToolbar color='light'>
          <IonButtons>
            <IonButton
              data-testid='DropdownModalCancel'
              className='btn btn-secondary'
              expand='block'
              onClick={dismiss}
              id='Multisection-modal-cancel-button'>
              {t('common.buttons.Cancel')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </>
  )

  return (
    <div>
      <IonModal
        id='Multisection-select'
        data-testid='DropdownModal'
        showBackdrop={showModal}
        isOpen={showModal}
        onDidDismiss={resetModalState}
        className={'multisectionPicklist'}
        backdropDismiss>
        {content}
      </IonModal>

      <IonPopover
        className='multisectionPicklist-popover'
        data-testid='DropdownPopover'
        showBackdrop={popoverState.showPopover}
        isOpen={popoverState.showPopover}
        event={popoverState.event}
        onDidDismiss={resetPopoverState}>
        {content}
      </IonPopover>

      <IonItem
        disabled={disabled}
        data-testid={dataTestID}
        lines='none'
        className='multisection-select-ion-item ion-no-padding'
        onClick={(e) => showDropdown(e)}>
        {label && (
          <IonLabel
            position={currentSelectedOption ? 'floating' : undefined}
            className={
              currentSelectedOption ? 'multisection-select-ion-label-floating' : 'multisection-select-ion-label'
            }
            color='medium'>
            {label}
          </IonLabel>
        )}
        {currentSelectedOption && (
          <div
            data-private={isPHI}
            style={getStyleForPHI(isPHI)}
            className='multisection-selected-option ion-no-padding'>
            {currentSelectedOption.name}
          </div>
        )}
        <IonIcon slot='end' className='ion-icon-end' size='small' color='medium' icon={caretDownOutline} />
      </IonItem>
    </div>
  )
}

export default MultisectionPicklist
